import type {GroupPipelineDetail} from '@internal/pages/group-pipeline/components/pipelineDisplay';

export type PipelineTask = GroupPipelineDetail['tasks'][number];

export type TaskActionKey =
  | 'complete'
  | 'passed'
  | 'failed'
  | 'uploadDocument'
  | 'draftChase'
  | 'gotResponse'
  | 'skip'
  | 'markComplete'
  | 'reopen'
  | 'sendBack'
  | 'changeCompletedDate'
  | 'rename'
  | 'changeDueDate';

export interface TaskAction {
  key: TaskActionKey;
  label: string;
  primary?: boolean;
  placeholder: boolean;
}

const LABELS: Record<TaskActionKey, string> = {
  complete: 'Complete',
  passed: 'Passed',
  failed: 'Failed',
  uploadDocument: 'Upload document',
  draftChase: 'Draft a chase',
  gotResponse: 'Got a response',
  skip: 'Skip this step',
  markComplete: 'Mark complete',
  reopen: 'Reopen',
  sendBack: 'Send back',
  changeCompletedDate: 'Change completed date',
  rename: 'Rename for this group',
  changeDueDate: 'Change the due date',
};

// Not built yet; they show a "coming soon" toast so the rules are visible.
export const PLACEHOLDER_ACTIONS = new Set<TaskActionKey>([
  'uploadDocument',
  'draftChase',
  'gotResponse',
]);

const action = (key: TaskActionKey, primary = false): TaskAction => ({
  key,
  label: LABELS[key],
  primary,
  placeholder: PLACEHOLDER_ACTIONS.has(key),
});

const completesByDocuments = (task: PipelineTask) =>
  task.rules.some((rule) => rule.type === 'completeWhenDocsReceived');

// Outreach only applies when the step is waiting on someone outside CBS that we chase.
const waitsOnEmployerOrBroker = (task: PipelineTask) =>
  task.actor === 'EMPLOYER' || task.actor === 'BROKER';

// How an open task gets finished, before outreach is considered.
function finishActions(task: PipelineTask): TaskAction[] {
  if (completesByDocuments(task)) return [action('uploadDocument')];
  if (task.isRepeatable) return [action('failed'), action('passed')];
  return [action('complete')];
}

// Buttons on the Next move card, driven by the step's config: its rules, whether it repeats,
// and who acts. Exactly one is primary.
export function nextMoveActions(task: PipelineTask): TaskAction[] {
  const actions = finishActions(task);
  if (waitsOnEmployerOrBroker(task)) {
    return [...actions, action('draftChase'), action('gotResponse', true)];
  }
  const last = actions.length - 1;
  return actions.map((a, i) => (i === last ? {...a, primary: true} : a));
}

// Inline buttons on an open task's row.
export function rowActions(task: PipelineTask): TaskAction[] {
  return task.status === 'OPEN' ? finishActions(task) : [];
}

// The row's pencil menu: corrections rather than the main way forward.
export function menuActions(task: PipelineTask): TaskAction[] {
  switch (task.status) {
    case 'OPEN':
      return [
        // Documents can't be uploaded yet, so these steps need a manual way to finish.
        ...(completesByDocuments(task) ? [action('markComplete')] : []),
        action('rename'),
        action('changeDueDate'),
        action('skip'),
      ];
    case 'NOT_STARTED':
      return [action('rename'), action('skip')];
    case 'COMPLETED':
      return [
        action('changeCompletedDate'),
        ...(task.isRepeatable ? [action('sendBack')] : []),
        action('reopen'),
      ];
    case 'SKIPPED':
      return [action('reopen')];
  }
}
